import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Copy, SvgX } from '@grubhubprod/gh-cookbook-react';
import { DataTable } from 'src/components/DataTable';
import { ReportStatusIcon } from 'src/components/enterprise/features/Reporting/ManageReportsPage/components/ReportStatusIcon/ReportStatusIcon';
import { ReportsStatusLegend } from 'src/components/enterprise/features/Reporting/ManageReportsPage/components/ReportsTable/ReportsStatusLegend';
import { createReportsAdapter } from 'src/components/enterprise/features/Reporting/ManageReportsPage/components/ReportsWrapper/hooks/createReportsAdapter';
import { ReportDownloadButton } from 'src/components/enterprise/features/Reporting/ManageReportsPage/components/ReportsTable/ReportDownloadButton';
import { ReportUI } from 'src/components/enterprise/features/Reporting/ManageReportsPage/utils/reportUI.utils';

enum ReportsTablePhoneColumnSpan {
  Status = 1,
  ReportName = 3,
  DateRequested = 2,
  Type = 2,
  DateRange = 3,
  Actions = 1,
}

export const ReportsTable: React.FC = () => {
  const { corpid: corpId } = useParams<{ corpid: string }>();
  const adapter = useMemo(() => createReportsAdapter(corpId), [corpId]);

  return (
    <DataTable.Provider adapter={adapter}>
      <DataTable.Container>
        <DataTable.TitleBar>
          <DataTable.Title x="sm">Available Reports</DataTable.Title>
          <DataTable.Summary />
        </DataTable.TitleBar>
        <DataTable.Toolbar justifyContent="space-between">
          <ReportsStatusLegend />
          <DataTable.ButtonLink
            useBlockButton
            href={`/corporate/enterprise/${corpId}/reports/create`}
          >
            Create Report
          </DataTable.ButtonLink>
        </DataTable.Toolbar>
        <DataTable.Content>
          <DataTable.Header>
            <DataTable.Column phone={ReportsTablePhoneColumnSpan.Status}>
              Status
            </DataTable.Column>
            <DataTable.Column phone={ReportsTablePhoneColumnSpan.ReportName}>
              Report Name
            </DataTable.Column>
            <DataTable.Column phone={ReportsTablePhoneColumnSpan.DateRequested}>
              Date Requested
            </DataTable.Column>
            <DataTable.Column phone={ReportsTablePhoneColumnSpan.Type}>
              Type
            </DataTable.Column>
            <DataTable.Column phone={ReportsTablePhoneColumnSpan.DateRange}>
              Date Range
            </DataTable.Column>
          </DataTable.Header>
          <DataTable.Body>
            {(report: ReportUI): JSX.Element => (
              <DataTable.Row key={report.exportId} id={report.exportId}>
                <DataTable.Cell phone={ReportsTablePhoneColumnSpan.Status}>
                  <ReportStatusIcon displayStatus={report.displayStatus} />
                </DataTable.Cell>
                <DataTable.Cell phone={ReportsTablePhoneColumnSpan.ReportName}>
                  <Copy variant="body">{report.exportName}</Copy>
                </DataTable.Cell>
                <DataTable.Cell
                  phone={ReportsTablePhoneColumnSpan.DateRequested}
                >
                  <Copy variant="body">{report.requestedDate}</Copy>
                </DataTable.Cell>
                <DataTable.Cell phone={ReportsTablePhoneColumnSpan.Type}>
                  <Copy variant="body">{report.reportDisplayName}</Copy>
                </DataTable.Cell>
                <DataTable.Cell phone={ReportsTablePhoneColumnSpan.DateRange}>
                  <Copy variant="body">{report.dateRange}</Copy>
                </DataTable.Cell>
                <DataTable.Cell
                  phone={ReportsTablePhoneColumnSpan.Actions}
                  justifyContent="flex-end"
                >
                  <ReportDownloadButton
                    corpId={corpId}
                    exportId={report.exportId}
                    exportName={report.exportName}
                    displayStatus={report.displayStatus}
                  />
                  <Button
                    variant="tertiary"
                    type="button"
                    className="tableActionButtons"
                    aria-label={`Delete ${report.exportName}`}
                    title={`Delete ${report.exportName}`}
                  >
                    <SvgX size="20px" aria-hidden />
                  </Button>
                </DataTable.Cell>
              </DataTable.Row>
            )}
          </DataTable.Body>
        </DataTable.Content>
      </DataTable.Container>
    </DataTable.Provider>
  );
};
